import { useMount } from '@fluentui/react-hooks';
import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  DependencyList,
  EffectCallback,
} from 'react';
import type { IAsyncQueryController } from 'component/Search/types';
/** Executes the specified callback debounced and after a
 * delay, any time the passed dependencies change.
 *
 * This is meant to be useful for scenarios where we want
 * to automatically do something once a user has finished
 * changing settings.
 */
export const useInteractionDelayEffect = (
  cb: EffectCallback,
  deps: DependencyList
): {
  onInteract: () => void;
  queryControl: IAsyncQueryController;
  lastInteraction: number;
} => {
  const control = useInteractionDelayCallback(cb);
  useEffect(() => {
    let isMounted = true;
    if (isMounted) {
      control.onInteract();
    }
    return () => {
      isMounted = false;
    };
    // incorrect exhaustive-deps:
    // (onInteract derives from useState)
    // don't execute an effect that sets state upon the change
    // of the output of that state
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return control;
};

/** Executes the specified callback any time the passed dep
 */
const useInteractionDelayCallback = (
  cb: EffectCallback
): {
  onInteract: () => void;
  queryControl: IAsyncQueryController;
  lastInteraction: number;
} => {
  const [lastInteraction, setLastInteraction] = useState(0);
  const [delay, setDelay] = useState<number | undefined>();
  const menuOpen = useRef(false);

  const stopTimeoutForQuery = useCallback(() => {
    clearTimeout(delay);
    setDelay(undefined);
  }, [delay]);
  /**
   * Call this to trigger a query immediately and
   * cancel timeout
   */
  const reQuery = useCallback(() => {
    stopTimeoutForQuery();
    cb();
  }, [cb, stopTimeoutForQuery]);

  // Fetch the current page the moment its opened so the user doesn't have to wait.
  useMount(() => {
    reQuery();
  });

  const startTimeoutForQuery = useCallback(() => {
    clearTimeout(delay);

    const timeout = setTimeout(() => {
      reQuery();
    }, 2100);
    setDelay(timeout as unknown as number);
  }, [delay, reQuery]);

  const onMenuOpen = useCallback(() => {
    stopTimeoutForQuery();
    menuOpen.current = true;
  }, [stopTimeoutForQuery]);

  const onInteract = useCallback(() => {
    setLastInteraction(Date.now());
    if (!menuOpen.current) {
      startTimeoutForQuery();
    }
  }, [menuOpen, startTimeoutForQuery]);
  const onMenuClose = useCallback(() => {
    menuOpen.current = false;
    onInteract();
  }, [onInteract]);
  const queryControl = useMemo(
    () => ({
      stop: stopTimeoutForQuery,
      start: startTimeoutForQuery,
      delay: onInteract,
      onMenuOpen,
      onMenuClose,
    }),
    [
      onInteract,
      onMenuClose,
      onMenuOpen,
      startTimeoutForQuery,
      stopTimeoutForQuery,
    ]
  );

  return { onInteract, queryControl, lastInteraction };
};
